import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { IEvidence, IUser } from "@interfaces/Interfaces";
import { evidenceAtom, userAtom } from "@state/State";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { userRolesSelector } from "@state/UserState";
import { useProgramData } from "@hooks/data/useProgramData";
import DataController from "@controllers/DataController";
import { Capacitor } from "@capacitor/core";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonContent,
  IonSpinner,
  IonButton,
} from "@ionic/react";
import HeaderTitle from "@components/common/HeaderTitle";
import ProgramInfoCard from "@components/home/ProgramInfoCard";
import ProgramCardButton from "@components/programs/ProgramCardButton";
import { Icon_Add_Evidence, Icon_Export_Blue, Icon_Refresh } from "@assets/images";
import ProgramEvidenceCard from "@components/programs/ProgramEvidenceCard";
import * as ProgramUtils from "@utils/programUtils";
import { useRefreshProgramData } from "@hooks/programs/useRefreshProgramData";
import { PROGRAM_IDS, PROGRAM_SKILL_IDS } from "@constants/programs";
import * as FirebaseService from "@controllers/FirebaseService";
import { EventRegister } from "react-native-event-listeners";
import { format } from "date-fns";
import WriteBlob from "capacitor-blob-writer";
import { Directory } from "@capacitor/filesystem";
import { FileOpener } from "@capacitor-community/file-opener";
import { saveAs } from "file-saver";
import * as EvidenceUtils from "@utils/evidenceUtils";
import _ from "lodash";

const MedicalImagingPage: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ programId: string }>();

  const { isRefreshingProgramData, refreshUserProgramData } = useRefreshProgramData();
  const { userPrograms } = useProgramData();

  const [downloadingCSV, setDownloadingCSV] = useState<boolean>(false);

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const allEvidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const allUserRoles = useRecoilValue(userRolesSelector);

  const program = userPrograms.find((item) => item.ID === params.programId)!;
  const programRoles = allUserRoles.find((role) => role.programID === params.programId);

  let userRoles: string[] = [];

  if (programRoles) {
    userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
  }

  let programEvidence: IEvidence[] = [];

  if (allEvidence.state === "hasValue" && allEvidence.contents) {
    programEvidence = allEvidence.contents.filter((evidence: IEvidence) => {
      const evidenceJSON = evidence.evidenceJSON;
      const id = evidenceJSON?.programInfo?.programID || "";

      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return id === program?.ID && onHoldReset !== 1 && evidence.draft !== true;
    });

    const evidenceDefinitions = program["Evidence Definitions"] ?? [];
    const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "medical-imaging")!;

    programEvidence = programEvidence.filter((evidence) => {
      return evidence.evidenceJSON.programInfo.programEvidenceDefinitionID === evidenceDefinition.ID;
    });

    programEvidence = _.orderBy(programEvidence, "date", "desc");
  }

  const evidenceCardClicked = (item: IEvidence): void => {
    history.push(`/dashboard/program/${params.programId}/read/${item.id}`);
  };

  const showCSVDownloadButton = (): boolean => {
    // TODO: Find a better place to put this
    return [
      PROGRAM_IDS.SWAST_SP_AP_RP, // SWAST SP/AP/RP
    ].includes(program.ID);
  };

  const downloadCSVDocument = async (): Promise<void> => {
    setDownloadingCSV(true);

    try {
      const evidenceDefinitions = program["Evidence Definitions"] ?? [];
      const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "medical-imaging")!;

      const csv = EvidenceUtils.generateRadiologyAuditsCSV({
        allEvidence: programEvidence,
        evidenceDefinition,
      });
      const blob = new Blob([csv], { type: "text/csv" });
      const filename = `Radiology-Audit-${format(new Date(), "dd-MM-yyyy")}`;
      if (await DataController.isWebAsync()) {
        saveAs(blob, filename.trim().length > 0 ? filename.trim() : "Untitled");
      } else {
        const filePath = await WriteBlob({
          path: `ParaFolio/Exports/CSV/${filename.trim().length > 0 ? filename.trim() : "Untitled"}.csv`,
          blob,
          directory: Directory.Library,
          recursive: true,
        });
        await FileOpener.open({
          filePath,
          contentType: "text/csv",
          openWithDefault: false,
        });
      }
    } catch (error) {
      console.error(error);
      window.alert("Failed to download Radiology Audit");
    } finally {
      setDownloadingCSV(false);
    }
  };

  const addEvidence = (): void => {
    const usesAdditionalEvidenceDefinitions = program.UsesAdditionalEvidenceDefinitions;
    const evidenceDefinitions = program["Evidence Definitions"] ?? [];
    const mandatoryStandards = ProgramUtils.getMandatoryStandards(user.contents, program);
    const optionalStandards = ProgramUtils.getOptionalStandards(user.contents, program);

    const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "medical-imaging");
    const learningObjectives = ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]);
    const skills = ProgramUtils.getSkillsForRole(user.contents, program);
    const preselectedSkill = skills.find((item) => item.ID === PROGRAM_SKILL_IDS.SWAST_SP_AP_RP_MEDICAL_IMAGING);

    const data = {
      program: program,
      ...(evidenceDefinitions.length > 0 &&
        usesAdditionalEvidenceDefinitions && {
          evidenceDefinitions: evidenceDefinition,
        }),
      ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
      ...(optionalStandards.length > 0 && { optionalStandards }),
      ...(learningObjectives.length > 0 && { learningObjectives }),
      ...(skills.length > 0 && { skills }),
      ...(preselectedSkill && { preselectedSkill }),
    };

    FirebaseService.logEvent("program_add_enhanced_evidence", {
      program: program.Name,
    });

    EventRegister.emit("evidence/add-program-evidence", data);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref={`/dashboard/program/${params.programId}`}
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{"Medical Imaging"}</HeaderTitle>
          <IonButtons slot="end">
            <IonButton
              disabled={isRefreshingProgramData}
              className="header-button"
              mode="ios"
              onClick={refreshUserProgramData}
            >
              {isRefreshingProgramData ? (
                <IonSpinner className="h-5 w-11 text-white" />
              ) : (
                <img
                  src={Icon_Refresh}
                  className="headerIcon"
                />
              )}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background">
        <ProgramInfoCard
          title="Medical Imaging"
          subTitle={program.MedicalImagingDescription}
          isHTMLSubtitle
        >
          {showCSVDownloadButton() && (
            <ProgramCardButton
              buttonPressed={() => downloadCSVDocument()}
              buttonText="Download Radiology Audit"
              icon={Icon_Export_Blue}
              iconClassName="w-10"
              showSpinner={downloadingCSV}
              disabled={downloadingCSV}
            />
          )}
          <ProgramCardButton
            buttonPressed={() => addEvidence()}
            buttonText="Add new evidence"
            icon={Icon_Add_Evidence}
          />
        </ProgramInfoCard>
        <ProgramInfoCard
          title="Evidence list"
          subTitle={programEvidence.length === 0 ? "Any evidence added  will appear below." : undefined}
        >
          {programEvidence.length > 0 && (
            <div className="mt-3">
              {programEvidence.map((item) => (
                <ProgramEvidenceCard
                  key={item.id}
                  buttonPressed={() => evidenceCardClicked(item)}
                  programEvidence={item}
                  program={program}
                  progressCheckLocked={false}
                />
              ))}
            </div>
          )}
        </ProgramInfoCard>
      </IonContent>
    </IonPage>
  );
};

export default MedicalImagingPage;
